import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { holdingsApi } from '@/services/api';
import { CurrencyCode } from '@/utils/formatters';
import { Holdings, HoldingsSummary, Client } from '@/types/client';
import { useClientApi } from '@/hooks/useClientApi';
import shared from '@/components/utils/shared.module.css';
import { PageFrame, PageHeader, Surface, Button, Note, Placeholder } from '@/components/ui';

const HoldingsDetail: React.FC = () => {
  const { getClient } = useClientApi();
  const { clientId } = useParams<{ clientId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [holdings, setHoldings] = useState<Holdings[]>([]);
  const [summary, setSummary] = useState<HoldingsSummary | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  // Only spin when there is something to load — without a client or date the
  // fetch is skipped and loading would never clear.
  const [loading, setLoading] = useState(() => !!clientId && !!searchParams.get('date'));
  const [error, setError] = useState<string | null>(null);

  const selectedDate = searchParams.get('date') || '';

  const loadData = useCallback(async () => {
    if (!clientId || !selectedDate) return;
    try {
      setLoading(true);
      setError(null);
      const [clientData, holdingsData] = await Promise.all([
        getClient(parseInt(clientId)),
        holdingsApi.getHoldingsByClient({ date: selectedDate, client_master_id: parseInt(clientId) })
      ]);
      setClient(clientData);
      setHoldings(holdingsData.results);
      setSummary(holdingsData.summary);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.message || 'Failed to load holdings data');
    } finally {
      setLoading(false);
    }
  }, [clientId, selectedDate]);

  useEffect(() => {
    if (clientId && selectedDate) loadData();
  }, [loadData, clientId, selectedDate]);

  const getCurrencySymbol = (code: string | undefined): string => {
    if (code === CurrencyCode.INR) return '₹';
    if (code === CurrencyCode.USD) return '$';
    return '';
  };

  const formatNumber = (value: number | undefined, decimals = 2): string => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value);
  };

  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });

  const calculatePercentage = (value: number | undefined): string => {
    if (value === undefined || value === null) return 'NaN';
    if (!summary?.total_market_value_base || summary.total_market_value_base === 0) return '0.00';
    return ((value / summary.total_market_value_base) * 100).toFixed(2);
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <PageFrame header={<PageHeader title="Holdings Detail" />}>
        <Surface>
          <Placeholder kind="loading" message="Loading holdings…" />
        </Surface>
      </PageFrame>
    );
  }

  /* ── Error ── */
  if (error) {
    return (
      <PageFrame
        header={
          <PageHeader
            title="Holdings Detail"
            actions={
              <Button onClick={() => navigate('/holdings')} icon={<ArrowLeft size={15} />}>
                Back to Holdings
              </Button>
            }
          />
        }
      >
        <Note tone="error">{error}</Note>
      </PageFrame>
    );
  }

  const currSymbol = getCurrencySymbol(summary?.base_currency_code);
  const totalPL = (summary?.total_market_value_base || 0) - (summary?.total_cost_value_base || 0);

  /* The summary cards become the page header's stat tiles — same figures, and
     they now sit where every other page puts its headline numbers. */
  const stats = summary
    ? [
        { label: 'Total holdings', value: summary.holdings_count || 0 },
        { label: 'Market value', value: `${currSymbol} ${formatNumber(summary.total_market_value_base)}` },
        { label: 'Cost value', value: `${currSymbol} ${formatNumber(summary.total_cost_value_base)}` },
        {
          label: 'Unrealised P&L',
          value: `${currSymbol} ${formatNumber(totalPL)}`,
          delta: totalPL >= 0 ? 'Gain' : 'Loss',
          deltaTone: (totalPL >= 0 ? 'up' : 'down') as 'up' | 'down',
        },
        { label: 'Base currency', value: `${summary.base_currency_code} (${currSymbol})` },
      ]
    : undefined;

  return (
    <PageFrame
      header={
        <PageHeader
          title="Holdings Detail"
          subtitle={
            client
              ? `${client.client_code} — ${client.full_name || `${client.first_name} ${client.last_name}`}${
                  selectedDate ? ` · as on ${formatDate(selectedDate)}` : ''
                }`
              : undefined
          }
          actions={
            <Button onClick={() => navigate('/holdings')} icon={<ArrowLeft size={15} />}>
              Back
            </Button>
          }
          stats={stats}
        />
      }
    >
      <Surface>
        <div className={shared['table-scroll']}>
            <table className={shared.table}>
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th className={shared['col-mid']}>Company</th>
                  <th className={shared['text-right']}>Quantity</th>
                  <th className={shared['text-right']}>Unit Cost</th>
                  <th className={shared['text-right']}>Unit Price</th>
                  <th className={shared['text-right']}>Cost Value</th>
                  <th className={shared['text-right']}>Market Value</th>
                  <th className={shared['text-right']}>P&L</th>
                  <th className={shared['text-right']}>% of Portfolio</th>
                  <th className={shared['text-right']}>Exchange Rate</th>
                  <th>Currency</th>
                  <th className={shared['text-right']} title="Accrued Income">AI</th>
                </tr>
              </thead>
              <tbody>
                {holdings.length === 0 ? (
                  <tr>
                    <td colSpan={12} className={shared['empty-cell']}>
                      No holdings found for this client on {selectedDate ? formatDate(selectedDate) : 'the selected date'}.
                    </td>
                  </tr>
                ) : (
                  holdings.map((holding) => {
                    const pl = (holding.market_value_base || 0) - (holding.cost_value_base || 0);
                    const sym = getCurrencySymbol(holding.base_currency_code);
                    return (
                      <tr key={holding.id}>
                        <td>
                          <Link to={`/companies/${holding.company}`} className={shared.link}>
                            {holding.company_symbol}
                          </Link>
                        </td>
                        <td className={shared['col-mid']}>{holding.company_name}</td>
                        <td className={shared['text-right']}>{formatNumber(holding.adj_quantity, 3)}</td>
                        <td className={shared['text-right']}>{formatNumber(holding.adj_unit_cost, 4)}</td>
                        <td className={shared['text-right']}>{formatNumber(holding.adj_unit_price, 4)}</td>
                        <td className={shared['text-right']}>{sym} {formatNumber(holding.cost_value_base)}</td>
                        <td className={shared['text-right']}>{sym} {formatNumber(holding.market_value_base)}</td>
                        <td className={`${pl >= 0 ? shared.profit : shared.loss} ${shared['text-right']}`}>
                          {sym} {formatNumber(pl)}
                        </td>
                        <td className={shared['text-right']}>{calculatePercentage(holding.market_value_base)}%</td>
                        <td className={shared['text-right']}>
                          {holding.exchange_rate ? formatNumber(holding.exchange_rate, 4) : '-'}
                        </td>
                        <td>{holding.currency_code}</td>
                        <td className={shared['text-right']} title="Accrued Income">{sym} {formatNumber(holding.accrued_income)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {holdings.length > 0 && summary && (
                <tfoot>
                  <tr>
                    <th>Total</th>
                    <th colSpan={4}></th>
                    <th className={shared['text-right']}>{currSymbol} {formatNumber(summary.total_cost_value_base)}</th>
                    <th className={shared['text-right']}>{currSymbol} {formatNumber(summary.total_market_value_base)}</th>
                    <th className={`${totalPL >= 0 ? shared.profit : shared.loss} ${shared['text-right']}`}>
                      {currSymbol} {formatNumber(totalPL)}
                    </th>
                    <th className={shared['text-right']}>100.00%</th>
                    <th></th>
                    <th></th>
                    <th></th>
                  </tr>
                </tfoot>
              )}
            </table>
        </div>
      </Surface>
    </PageFrame>
  );
};

export default HoldingsDetail;